import { anyLeague } from "../../../classificators/league/index.js";
import { IterableDataProcessor, RankingStat } from "../../../entities/index.js";
import { BattingRankingStats, PitchingRankingStats, RankingScopeContext } from "../../../enums/index.js";
import EnumValueProvider from "../../../providers/EnumValueProvider.js";
import RankingStatCalculatorBase from "./../RankingStatCalculatorBase.js";
import NumberFormatter from "../../../formatters/NumberFormatter.js";
import ResultsSlice from "../../../valueObjects/ResultsSlice.js";
import satisfiesAll from "../../../classificators/satisfiesAll.js";
import battingScopeContext from "../../../classificators/resultsSlice/battingScopeContext.js";
import RankingStatCaseVerifier from "../../../validators/RankingStatCaseVerifier.js";
import sessionWithStatGrabScoring from "../../../classificators/session/sessionWithStatGrabScoring.js";
import pitchingScopeContext from "../../../classificators/resultsSlice/pitchingScopeContext.js";
import NewBaseHitsCalculator from './NewBaseHitsCalculator.js';
import NewStrikeoutsCalculator from './NewStrikeoutsCalculator.js';
import NewAtBatsCalculator from './NewAtBatsCalculator.js';
import NewSacrificeStatsCalculator from './NewSacrificeStatsCalculator.js';
import resultsSliceForMatchup from "../../../classificators/resultsSlice/resultsSliceForMatchup.js";

class NewBattingAverageOnBallsInPlayCalculator extends RankingStatCalculatorBase {
    constructor(
        context, customResultsProcessor, rankingStatCaseVerifier, numberFormatter,
        strikeoutCalculator, hitsCalculator, atBatsCalculator, sacFliesCalculator
    ) {
        if (typeof(customResultsProcessor) !== 'undefined' && !(customResultsProcessor instanceof IterableDataProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from IterableDataProcessor class');
        }
        if (typeof(numberFormatter) !== 'undefined' && !(numberFormatter instanceof NumberFormatter)) {
            throw new TypeError('numberFormatter must be an instance inheriting from NumberFormatter class');
        }
        if (typeof(stolenBaseClassifier) !== 'undefined' && typeof(stolenBaseClassifier) !== 'function') {
            throw new TypeError('stolenBaseClassifier must be an function or undefined');
        }
        if (typeof(caughtStealingClassifier) !== 'undefined' && typeof(caughtStealingClassifier) !== 'function') {
            throw new TypeError('caughtStealingClassifier must be an function or undefined');
        }
        if (typeof(runClassifier) !== 'undefined' && typeof(runClassifier) !== 'function') {
            throw new TypeError('runClassifier must be an function or undefined');
        }
        if (!context || typeof(context) !== 'string') {
            throw new TypeError('context must be an non empty string');
        }
        if (![RankingScopeContext.Pitching.key, RankingScopeContext.Batting.key].find(s => s === context)) {
            throw new Error(`type of context ${context} in not supported, only ${RankingScopeContext.Pitching.key} and ${RankingScopeContext.Batting.key} are supported`);
        }

        if (typeof(strikeoutCalculator) !== 'undefined' && !(strikeoutCalculator instanceof RankingStatCalculatorBase)) {
            throw new TypeError('strikeoutCalculator must be an instance inheriting from RankingStatCalculatorBase class');
        }
        if (typeof(hitsCalculator) !== 'undefined' && !(hitsCalculator instanceof RankingStatCalculatorBase)) {
            throw new TypeError('hitsCalculator must be an instance inheriting from RankingStatCalculatorBase class');
        }
        if (typeof(atBatsCalculator) !== 'undefined' && !(atBatsCalculator instanceof RankingStatCalculatorBase)) {
            throw new TypeError('atBatsCalculator must be an instance inheriting from RankingStatCalculatorBase class');
        }
        if (typeof(sacFliesCalculator) !== 'undefined' && !(sacFliesCalculator instanceof RankingStatCalculatorBase)) {
            throw new TypeError('sacFliesCalculator must be an instance inheriting from RankingStatCalculatorBase class');
        }

        const caseVerifier = rankingStatCaseVerifier || new RankingStatCaseVerifier(
            anyLeague,
            sessionWithStatGrabScoring,
            satisfiesAll(
                context === RankingScopeContext.Batting.key ? battingScopeContext: pitchingScopeContext,
                resultsSliceForMatchup
            )
        );

        super(
            context === RankingScopeContext.Batting.key
            ? [ BattingRankingStats.BattingAverageOnBallsInPlay ]
            : [ PitchingRankingStats.BattingAverageOnBallsInPlay ],
            caseVerifier
        );

        this.numberFormatter = numberFormatter || new NumberFormatter(3);
        this.resultsProcessor = customResultsProcessor || new IterableDataProcessor(
            new EnumValueProvider()
        );
        this.hitsCalculator = hitsCalculator || new NewBaseHitsCalculator(context, this.resultsProcessor, caseVerifier);
        this.strikeoutCalculator = strikeoutCalculator || new NewStrikeoutsCalculator(context, this.resultsProcessor, caseVerifier);
        this.atBatsCalculator = atBatsCalculator || new NewAtBatsCalculator(context, this.resultsProcessor, caseVerifier);
        this.sacFliesCalculator = sacFliesCalculator || new NewSacrificeStatsCalculator(context, this.resultsProcessor, caseVerifier);
        this.context = context;
    }

    /**
     * Calculates given statistical parameter
     * @param {object} parameter - a parameter to calculate, object from Enum PitchingRankingStats, BattingRankingStats or GeneralRankingStats
     * @param {ResultsSlice} slice - result slice with scope
     * @param {object} session - session object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/session-v2.json
     * @param {object} league - league object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/league.json
     * @returns {RankingStat|null} RankingStat instance or null when calculation wasn't possible
     */
    calculateStat(parameter, slice, session, league) {
        if (!(slice instanceof ResultsSlice)) {
            throw new TypeError('slice must be an instance inheriting from ResultsSlice class');
        }

        let hits;
        let homeRuns;
        let atBats;
        let sacFlies;
        let strikeouts;

        if (this.context === RankingScopeContext.Batting.key) {
            // TODO - throw or return nulls ?
            if (!this.strikeoutCalculator.canCalculate(
                BattingRankingStats.Strikeouts, slice, session, league
            )) {
                throw new Error(`strikeoutCalculator is not able to calculate necessary stats - ${BattingRankingStats.Strikeouts.key}`);
            }
            strikeouts = this.strikeoutCalculator.calculate(BattingRankingStats.Strikeouts, slice, session, league);

            if (!this.hitsCalculator.canCalculate(
                BattingRankingStats.Hits, slice, session, league
            )) {
                throw new Error(`hitsCalculator is not able to calculate necessary stats - ${BattingRankingStats.Hits.key}`);
            }
            hits = this.hitsCalculator.calculate(BattingRankingStats.Hits, slice, session, league);

            if (!this.hitsCalculator.canCalculate(
                BattingRankingStats.HomeRuns, slice, session, league
            )) {
                throw new Error(`hitsCalculator is not able to calculate necessary stats - ${BattingRankingStats.HomeRuns.key}`);
            }
            homeRuns = this.hitsCalculator.calculate(BattingRankingStats.HomeRuns, slice, session, league);

            if (!this.atBatsCalculator.canCalculate(
                BattingRankingStats.AtBats, slice, session, league
            )) {
                throw new Error(`atBatsCalculator is not able to calculate necessary stats - ${BattingRankingStats.AtBats.key}`);
            }
            atBats = this.atBatsCalculator.calculate(BattingRankingStats.AtBats, slice, session, league);

            if (!this.sacFliesCalculator.canCalculate(
                BattingRankingStats.SacrificeFlies, slice, session, league
            )) {
                throw new Error(`sacFliesCalculator is not able to calculate necessary stats - ${BattingRankingStats.SacrificeFlies.key}`);
            }
            sacFlies = this.sacFliesCalculator.calculate(BattingRankingStats.SacrificeFlies, slice, session, league);
        }
        else if (this.context === RankingScopeContext.Pitching.key) {
            // TODO - throw or return nulls ?
            if (!this.strikeoutCalculator.canCalculate(
                PitchingRankingStats.Strikeouts, slice, session, league
            )) {
                throw new Error(`strikeoutCalculator is not able to calculate necessary stats - ${PitchingRankingStats.Strikeouts.key}`);
            }
            strikeouts = this.strikeoutCalculator.calculate(PitchingRankingStats.Strikeouts, slice, session, league);

            if (!this.hitsCalculator.canCalculate(
                PitchingRankingStats.HitsAllowed, slice, session, league
            )) {
                throw new Error(`hitsCalculator is not able to calculate necessary stats - ${PitchingRankingStats.HitsAllowed.key}`);
            }
            hits = this.hitsCalculator.calculate(PitchingRankingStats.HitsAllowed, slice, session, league);

            if (!this.hitsCalculator.canCalculate(
                PitchingRankingStats.HomeRunsAllowed, slice, session, league
            )) {
                throw new Error(`hitsCalculator is not able to calculate necessary stats - ${PitchingRankingStats.HomeRunsAllowed.key}`);
            }
            homeRuns = this.hitsCalculator.calculate(PitchingRankingStats.HomeRunsAllowed, slice, session, league);

            if (!this.atBatsCalculator.canCalculate(
                PitchingRankingStats.AtBats, slice, session, league
            )) {
                throw new Error(`atBatsCalculator is not able to calculate necessary stats - ${PitchingRankingStats.AtBats.key}`);
            }
            atBats = this.atBatsCalculator.calculate(PitchingRankingStats.AtBats, slice, session, league);

            if (!this.sacFliesCalculator.canCalculate(
                PitchingRankingStats.SacrificeFlies, slice, session, league
            )) {
                throw new Error(`sacFliesCalculator is not able to calculate necessary stats - ${PitchingRankingStats.SacrificeFlies.key}`);
            }
            sacFlies = this.sacFliesCalculator.calculate(PitchingRankingStats.SacrificeFlies, slice, session, league);
        }

        const strikeoutsValue = (!strikeouts || strikeouts.isEmpty()) ? 0 : strikeouts.value;
        const hitsValue = (!hits || hits.isEmpty()) ? 0 : hits.value;
        const homeRunsValue = (!homeRuns || homeRuns.isEmpty()) ? 0 : homeRuns.value;
        const atBatsValue = (!atBats || atBats.isEmpty()) ? 0 : atBats.value;
        const sacFliesValue = (!sacFlies || sacFlies.isEmpty()) ? 0 : sacFlies.value;

        // BABIP measures a player's batting average exclusively on balls hit into the field of play, removing outcomes not affected by the opposing defense (namely home runs and strikeouts).
        // For example, a hitter who goes 2-for-5 with a home run and a strikeout would have a .333 BABIP. He's 1-for-3 on the balls he put in play.
        // The formula
        // (H - HR)/(AB - K - HR + SF)
        // where H is hits, HR is home runs, AB is at bats, K is strikeouts, and SF is sacrifice flies.
        // @see https://www.mlb.com/glossary/advanced-stats/babip 
        // @see https://en.wikipedia.org/wiki/Batting_average_on_balls_in_play
        const weight = (atBatsValue - strikeoutsValue - homeRunsValue + sacFliesValue);
        const babip = (hitsValue - homeRunsValue) / weight;
        
        if (weight === 0) {
            // TODO - this is strange, but for totally untagged and non scored games it can happen
            return null;
        }

        return RankingStat.fromParameter(
            this.supportedParams[0], babip, weight
        );
    }
}

export default NewBattingAverageOnBallsInPlayCalculator;